// Setup › Finance › Compensation (`/c/{slug}/setup/finance/compensation`; 11 §Seats carry their
// cost, 05 §finance, M5-03 `compensation.*`): what each seat pays its person, monthly, hourly or by
// contract, from a date. Pay is never posted to the cost ledger: the finance views accrue it when
// they are read (monthly pay for every month it is in force; hourly and contract pay × the
// approved time on the seat), so scorecards and the ledger never carry an individual's pay.
// Restricted: finance admins and the owner read every row, a pay viewer their seat's; finance
// admins write. Calls on load: the standing, then the rows; the people and seats for names.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  EmptyState,
  Input,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BanknoteIcon, CalendarXIcon, PencilIcon, PlusIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { toMinor } from "../costs/costs-page";
import { formatDate, formatMoney, fractionDigits } from "../format";
import { peopleQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";
import {
  type Currency,
  CurrencySelect,
  FinancePage,
  FormDialog,
  FormField,
  ReadOnlyNotice,
  RestrictedNotice,
  useFinanceStanding,
  useOrgNames,
} from "./finance-parts";
import { compensationQuery, financeKeys } from "./finance-queries";

type Comp = Awaited<ReturnType<Api["client"]["compensation"]["list"]>>["data"][number];
type Cadence = Comp["cadence"];

export const CADENCE_TEXT: Record<Cadence, string> = {
  monthly: "a month",
  hourly: "an hour",
  contract: "an hour (contract)",
};

const major = (minor: number, currency: string) =>
  (minor / 10 ** fractionDigits(currency)).toFixed(fractionDigits(currency));
const today = () => new Date().toISOString().slice(0, 10);

export function CompensationScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const standing = useFinanceStanding(api, company);
  const reads = standing.restricted || standing.compSeats.size > 0;
  const comp = useInfiniteQuery({ ...compensationQuery(api, company.id), enabled: reads });
  const people = useInfiniteQuery(peopleQuery(api, company.id));
  const names = useOrgNames(api, company);
  const [editing, setEditing] = useState<Comp | "new" | null>(null);
  const qc = useQueryClient();
  const end = useMutation({
    mutationFn: (row: Comp) =>
      api.client.compensation.update({
        params: { co: company.id, id: row.id },
        body: { effective_to: today() < row.effective_from ? row.effective_from : today() },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: financeKeys.all(company.id) }),
  });
  const persons = (people.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (p) => p.kind === "person",
  );
  const personName = (id: string | null) =>
    id
      ? (persons.find((p) => p.id === id)?.display_name ?? `Member …${id.slice(-4)}`)
      : "Whoever holds it";
  const rows = comp.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <FinancePage
      company={company}
      where="setup"
      title="Compensation"
      icon={BanknoteIcon}
      actions={
        standing.admin ? (
          <Button size="sm" onClick={() => setEditing("new")}>
            <PlusIcon aria-hidden="true" />
            Set pay
          </Button>
        ) : null
      }
    >
      {standing.query.isPending ? (
        <Loading label="Loading your finance access" rows={2} />
      ) : standing.query.isError ? (
        <LoadError what="Your finance access" error={standing.query.error} />
      ) : !reads ? (
        <RestrictedNotice company={company} what="Pay rows" />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {!standing.admin ? <ReadOnlyNotice company={company} owner={standing.owner} /> : null}
          {comp.isPending ? (
            <Loading label="Loading compensation" rows={3} />
          ) : comp.isError ? (
            <LoadError what="Compensation" error={comp.error} />
          ) : rows.length === 0 ? (
            <EmptyState
              variant="card"
              icon={BanknoteIcon}
              title="No pay set"
              description="Set a seat's pay so budget vs actual, margins and forecasts carry it."
            />
          ) : (
            <>
              <Table data-slot="compensation">
                <TableCaption>
                  Hourly and contract pay is priced on the seat's approved hours.
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Seat</TableHead>
                    <TableHead scope="col" className="max-md:hidden">
                      Person
                    </TableHead>
                    <TableHead scope="col" className="text-right">
                      Pay
                    </TableHead>
                    <TableHead scope="col" className="max-sm:hidden">
                      In force
                    </TableHead>
                    <TableHead scope="col">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} className="group/row" data-comp={r.id}>
                      <TableHead scope="row" className="font-normal">
                        {names.seatName(r.seat_id)}
                      </TableHead>
                      <TableCell className="max-md:hidden">{personName(r.membership_id)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(r.amount_minor, r.currency)} {CADENCE_TEXT[r.cadence]}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-sm:hidden">
                        {formatDate(r.effective_from)}
                        {r.effective_to ? ` – ${formatDate(r.effective_to)}` : " onwards"}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {standing.admin ? (
                          <RowActions>
                            <RowAction
                              icon={PencilIcon}
                              label={`Edit pay of ${names.seatName(r.seat_id)}`}
                              tip="Edit"
                              onClick={() => setEditing(r)}
                            />
                            {r.effective_to === null ? (
                              <ConfirmRowAction
                                icon={CalendarXIcon}
                                label={`End pay of ${names.seatName(r.seat_id)} today`}
                                tip="End today"
                                disabled={end.isPending}
                                title={`End the pay of ${names.seatName(r.seat_id)} today?`}
                                description="It stays on record up to today and stops costing the seat after."
                                confirmLabel="End today"
                                onConfirm={() => end.mutate(r)}
                              />
                            ) : null}
                          </RowActions>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                what="pay rows"
                hasMore={comp.hasNextPage}
                loading={comp.isFetchingNextPage}
                onMore={() => void comp.fetchNextPage()}
              />
            </>
          )}
        </div>
      )}
      {editing ? (
        <CompDialog
          api={api}
          company={company}
          row={editing === "new" ? null : editing}
          seats={names.seats}
          persons={persons}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </FinancePage>
  );
}

function CompDialog({
  api,
  company,
  row,
  seats,
  persons,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  row: Comp | null;
  seats: { id: string; title: string }[];
  persons: { id: string; display_name: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [seat, setSeat] = useState(row?.seat_id ?? seats[0]?.id ?? "");
  const [person, setPerson] = useState(row?.membership_id ?? "");
  const [cadence, setCadence] = useState<Cadence>(row?.cadence ?? "monthly");
  const [currency, setCurrency] = useState<Currency>((row?.currency as Currency) ?? "USD");
  const [amount, setAmount] = useState(row ? major(row.amount_minor, row.currency) : "");
  const [from, setFrom] = useState(row?.effective_from ?? today());
  const [to, setTo] = useState(row?.effective_to ?? "");
  const [note, setNote] = useState(row?.note ?? "");
  const ids = {
    seat: useId(),
    person: useId(),
    cadence: useId(),
    cur: useId(),
    amount: useId(),
    from: useId(),
    to: useId(),
    note: useId(),
  };
  const minor = toMinor(amount, currency);
  const shared = {
    membership_id: person || null,
    amount_minor: minor ?? 0,
    currency,
    effective_from: from,
    effective_to: to || null,
    note: note.trim() || null,
  };
  const save = useMutation({
    mutationFn: () =>
      row
        ? api.client.compensation.update({ params: { co: company.id, id: row.id }, body: shared })
        : api.client.compensation.create({
            params: { co: company.id },
            idempotencyKey: newIdempotencyKey(),
            body: { ...shared, seat_id: seat, cadence },
          }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={row ? "Edit pay" : "Set a seat's pay"}
      description="Only finance admins and the owner see it; a pay viewer sees their seat's."
      submitLabel={row ? "Save" : "Set pay"}
      pending={save.isPending}
      error={save.error}
      canSubmit={
        Boolean(seat) && minor !== null && minor >= 0 && Boolean(from) && (!to || to >= from)
      }
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {row ? null : (
          <FormField id={ids.seat} label="Seat">
            <NativeSelect id={ids.seat} value={seat} onChange={(e) => setSeat(e.target.value)}>
              {seats.map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.title}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </FormField>
        )}
        <FormField id={ids.person} label="Person" hint="Empty: whoever holds the seat.">
          <NativeSelect id={ids.person} value={person} onChange={(e) => setPerson(e.target.value)}>
            <NativeSelectOption value="">Whoever holds it</NativeSelectOption>
            {persons.map((p) => (
              <NativeSelectOption key={p.id} value={p.id}>
                {p.display_name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
        {row ? null : (
          <FormField id={ids.cadence} label="Paid">
            <NativeSelect
              id={ids.cadence}
              value={cadence}
              onChange={(e) => setCadence(e.target.value as Cadence)}
            >
              <NativeSelectOption value="monthly">Monthly salary</NativeSelectOption>
              <NativeSelectOption value="hourly">Hourly</NativeSelectOption>
              <NativeSelectOption value="contract">Contract (hourly rate)</NativeSelectOption>
            </NativeSelect>
          </FormField>
        )}
        <FormField
          id={ids.amount}
          label={`Amount ${CADENCE_TEXT[row?.cadence ?? cadence]}`}
          error={
            amount !== "" && (minor === null || minor < 0) ? "Enter an amount like 6500.00." : null
          }
        >
          <Input
            id={ids.amount}
            inputMode="decimal"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </FormField>
        <FormField id={ids.cur} label="Currency">
          <CurrencySelect id={ids.cur} value={currency} onChange={setCurrency} />
        </FormField>
        <FormField id={ids.from} label="From">
          <Input
            id={ids.from}
            type="date"
            required
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </FormField>
        <FormField
          id={ids.to}
          label="Until"
          error={to && to < from ? "Ends before it starts." : null}
          hint="Empty: until changed."
        >
          <Input id={ids.to} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </FormField>
      </div>
      <FormField id={ids.note} label="Note">
        <Input
          id={ids.note}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </FormField>
    </FormDialog>
  );
}
